import React from "react";
import { Alert, Button } from "antd";
import { useTranslation } from "react-i18next";
import * as TYPES from "@src/redux/types";

interface ReportExclusionNoteProps {
  exclusion?: TYPES.ReportExclusion;
  /** True when the current result set came back with no rows at all. */
  isEmptyResult: boolean;
  onShowAll: () => void;
}

/**
 * User-facing note for the no-finding exclusion.
 *
 * The report drops subjects and question columns whose answers are all blank
 * or "Not at all". That is the intended output, but silently removing rows and
 * columns is exactly what made earlier versions of this page look broken — so
 * say what was removed, and keep the way back one click away.
 */
const ReportExclusionNote: React.FC<ReportExclusionNoteProps> = ({
  exclusion,
  isEmptyResult,
  onShowAll,
}) => {
  const { t } = useTranslation();

  if (!exclusion?.isApplied) return null;

  const excludedQuestionCount = exclusion.excludedQuestionCount ?? 0;
  const excludedSubjectCount = exclusion.excludedSubjectCount ?? 0;

  // Nothing was actually removed and there is data on screen — no note needed.
  if (!isEmptyResult && excludedQuestionCount === 0 && excludedSubjectCount === 0) {
    return null;
  }

  const answerList = (exclusion.noFindingAnswerList ?? [])
    .map((answer) => `"${answer}"`)
    .join(", ");

  const removedPartList: string[] = [];
  if (excludedSubjectCount > 0) {
    removedPartList.push(
      `${excludedSubjectCount} ${
        excludedSubjectCount === 1
          ? t("subject") || "subject"
          : t("subjects") || "subjects"
      }`
    );
  }
  if (excludedQuestionCount > 0) {
    removedPartList.push(
      `${excludedQuestionCount} ${
        excludedQuestionCount === 1
          ? t("question") || "question"
          : t("questions") || "questions"
      }`
    );
  }

  const rule =
    t("report_exclusion_rule", { answers: answerList }) ||
    `Answers that are blank or ${answerList} are treated as no finding.`;

  const message = isEmptyResult
    ? t("report_exclusion_all_excluded") ||
      "No subject reported a finding for the selected questions."
    : `${removedPartList.join(" and ")} ${
        t("report_exclusion_excluded_suffix") || "excluded from this report."
      }`;

  return (
    <Alert
      type={isEmptyResult ? "warning" : "info"}
      showIcon
      className="report-exclusion-note"
      message={message}
      description={rule}
      action={
        <Button size="small" onClick={onShowAll}>
          {t("show_all") || "Show all"}
        </Button>
      }
    />
  );
};

export default ReportExclusionNote;
